import { useContext, useEffect, useState } from 'react';
import Button from '../../components/Button';
import BarcodeScanner from '../../helpers/BarcodeScanner';
import ScanCard from './ScanCard';
import { checkIsInventoryLabel } from '../../services/common';
import { getBoardScanDetail, getInventoryDetail, linkInventoryToBook, updateStation } from '../../services/station';
import { UserContext } from '../../context/UserContext';
import Modal from '../../components/Modal';
import Input from '../../components/Input';
import styles from "./ScanView.module.css";
import InfoMessage from '../../components/InfoMessage';
import MaterialIconsReact from 'material-icons-react';
import { uuid } from '../../helpers/Utils';
import Tag from "../../components/Tag";
import { useQueryClient } from '@tanstack/react-query';
import { rudderStackTrackEvent } from '../../plugins/analytics';

function ScanView() {
  const {workOrder, toastList, setToastList, selectedLocation, currentFactory, setShowLoadingFull, user} = useContext(UserContext);
  const [addManully, setAddManually] = useState(false);
  const [boardScanData, setBoardScanData] = useState({});
  const [barcode, setBarcode] = useState('');
  const [finishedParts, setFinishedParts] = useState([]);
  const [bookCutList, setBookCutList] = useState(undefined);
  const [selectedBook, setSelectedBook] = useState(undefined);
  const [showBookCut, setShowBookCut] = useState(false);
  const queryClient = useQueryClient()

  useEffect(() => {
    initializeBarCodeListener();
    return () => removeBarCodeListener()
    // add dependencies since state updates wont be listened in addEventListener
  }, [selectedBook, finishedParts, showBookCut, bookCutList, boardScanData])

  const handleScanListener = (event) => {
    rudderStackTrackEvent(
      "tars_beamsaw_board:on_barcode_scan",
      {
        user_id: `TARS${user.id}`,
        email: user.email,
        action: "scan",
        factory: currentFactory.item_code,
        location: selectedLocation.location
      }
    );
    onBarcodeScan(event.detail);
  }

  const initializeBarCodeListener = () => {
    BarcodeScanner.initialize();
    document.addEventListener('onbarcodescanned', handleScanListener)
  }

  const removeBarCodeListener = () => {
    document.removeEventListener('onbarcodescanned', handleScanListener)
    BarcodeScanner.close()
  }

  useEffect(() => {
    setBoardScanData({})
    setFinishedParts([]);
  }, [workOrder])

  const onBarcodeScan = async(barcode) => {
    if (showBookCut) {
      // ignore scan cases
      return;
    }
    setShowLoadingFull(true);
    try {
      let label = await checkIsInventoryLabel({
        code_map_id: barcode,
        _factory_code: currentFactory.item_code
      });
      if (selectedBook) {
        if (label.status === 200 && label.message.detail.found && label.message.type.startsWith("INVENTORY") === true) {
          const response = await getInventoryDetail({
            code_map_id : parseInt(label.message.id),
            _factory_code: currentFactory.item_code
          })
          if (response.status !== 200) {
            showToast({type: 'error', message: response.message, autoClose: false});
            setShowLoadingFull(false);
            return;
          }
          const inventoryData = response.message;
          if (inventoryData.found === false) {
            showToast({type: 'error', message: "Inventory Not Found", autoClose: false});
          } else if (inventoryData.inventory.type !== "BRD"){
            showToast({type: 'error', message: "Not a Valid Board Inventory", autoClose: false});
          } else if (inventoryData.inventory.qty === 0) {
            showToast({type: 'error', message: "The Board is not available in Stock", autoClose: false});
          } else if (selectedBook.length !== inventoryData.inventory.length || selectedBook.width !== inventoryData.inventory.width) {
            showToast({type: 'error', message: `Inventory Dimension ${inventoryData.inventory.length} x ${inventoryData.inventory.width} does not match the Book`, autoClose: false});
          } else if (selectedBook.material !== inventoryData.data.desc_1) {
            showToast({type: 'error', message: `Inventory Material(${inventoryData.data.desc_1}) Does not Match the Pattern(${selectedBook.material})`, autoClose: false});
          } else if (bookCutList.inventories.indexOf(inventoryData.inventory.id) >= 0) {
            showToast({type: 'error', message: "Board Is Already Scanned. Scan the Next Board", autoClose: false});
          } else if (bookCutList.inventories.length > selectedBook.boardcount) {
            showToast({type: 'error', message: "Number of Boards Exceeded the Book", autoClose: false})
          } else {
            setBookCutList({
              ...bookCutList,
              inventories: [
                ...bookCutList.inventories,
                inventoryData.inventory
              ]
            })
          }
        } else {
          showToast({type: 'error', message: "Not a Valid Board Inventory", autoClose: false});
        }
      } else {
        if (label.status === 200 && label.message.detail.found && label.message.type.startsWith("INVENTORY") == true) {
          await checkBoardScanDetail(label.message.id+"");
        } else if (!isNaN(barcode)) {
          await checkBoardScanDetail(barcode);
        } else {
          processPart(barcode);
        }
      }
    } catch (err) {
      console.log(err);
      showToast({type: 'error', message: err.message || err, autoClose: false});
    }
    setShowLoadingFull(false);
  }

  const checkBoardScanDetail = async (barcode) => {
    let boardScanData = await getBoardScanDetail({
      stockpile_id : workOrder.id,
      barcode,
      _factory_code: currentFactory.item_code,
      location: selectedLocation.location,
    });
    if (boardScanData.status != 200) {
      showToast({type: 'error', message: boardScanData.message || 'Scanning failed due to internal server error. Kindly try again', autoClose: false});
      return;
    }
    boardScanData = boardScanData.message;
    if (boardScanData.bookfound === true) {
      showToast({type: 'success', message: "Program has been sent to the machine", autoClose: false})
      setBoardScanData(boardScanData);
      setFinishedParts([]);
    } else {
      if (boardScanData.matches.length == 0) {
        showToast({type: 'error', message: "Inventory does not match any pattern in the Work Order", autoClose: false})
      } else if (boardScanData.matches.length === 1) {
        if (boardScanData.matches[0].boardcount === 0) {
          showToast({type: 'error', message: "Board Count is 0", autoClose: false})
        } else if (boardScanData.matches[0].boardcount === 1) {
          linkInventoryToBookAndLoad(boardScanData.matches[0], [boardScanData.inventory])
        } else {
          selectBook(boardScanData.matches[0], [boardScanData.inventory])
        }
      } else {
        setBookCutList({
          books: boardScanData.matches,
          inventories: [boardScanData.inventory]
        });
        setShowBookCut(true);
      }
    }
  }

  const showToast = ({type, message, action, autoClose = true}) => {
    // show toast message
    let toast = {
      type: type,
      text: message,
      onClick: action ? () => console.log('button clicked') : undefined,
      autoClose: autoClose
    }
    setToastList([...toastList, toast]);
  }

  const addBarCodeManul = () => {
    if (barcode.length > 5) {
      rudderStackTrackEvent(
        "tars_beamsaw_board:on_barcode_input",
        {
          user_id: `TARS${user.id}`,
          email: user.email,
          action: "input",
          event_time: new Date().toISOString(),
          factory: currentFactory.item_code,
          location: selectedLocation.location
        }
      );
      onBarcodeScan(barcode);
      setBarcode('');
      setAddManually(false);
    }
  }

  const processPart = async (barcode) => {
    const partScan = await updateStation({
      location: selectedLocation.location,
      part: barcode,
      reject: false,
      station: "beamsaw",
      userauthed: true,
      uuid: uuid(),
      _factory_code: currentFactory.item_code
    })
    queryClient.invalidateQueries({ queryKey: ['locationProductivity', 'boardData'] })
    if (partScan.status === 1011) {
      showToast({type: 'error', message: `The Part ${barcode} has passed this station before`, autoClose: false})
      return;
    }
    if (partScan.status === 200) {
      if (partScan.message.multipart_parent_id && partScan.message.multipart_parent_id.length > 2) {
        showToast({type: 'error', message: `This part belongs to Aluminium Section. Please inform them and hand it over`, autoClose: false})
        return;
      }
      setFinishedParts([...finishedParts, partScan.message.part]);
      showToast({type: 'success', message: `Part ${barcode} successfully scanned`, autoClose: false})
      return;
    }
    var message = ""
    try{
      var error = JSON.parse(partScan.message)
      message = error.error_description;
    } catch(err) {
      message = partScan.message
    }
    showToast({type: 'error', message, autoClose: false})
  }

  const linkInventoryToBookAndLoad = async (book, inventories) => {
    setShowLoadingFull(true);
    try {
      const boardScanData = await linkInventoryToBook({
        book_id : book.id,
        inventories : inventories.map((item) => item.id),
        _factory_code: currentFactory.item_code,
        location: selectedLocation.location
      })
      if (boardScanData.status != 200) {
        showToast({type: 'error', message: boardScanData.message || 'Scanning failed due to internal server error. Kindly try again', autoClose: false});
        setShowLoadingFull(false);
        return;
      }
      showToast({type: 'success', message: "Program has been sent to the machine", autoClose: false})
      setBoardScanData(boardScanData.message);
      setFinishedParts([]);
    } catch (err) {
      console.log(err);
      showToast({type: 'error', message: "Scanning failed due to internal server error. Kindly try again", autoClose: false})
    }
    setShowLoadingFull(false);
  }

  const handleCutAll = () => {
    if (selectedBook.boardcount == bookCutList.inventories.length) {
      setShowBookCut(false);
      setSelectedBook(undefined);
      setBookCutList(undefined);
      linkInventoryToBookAndLoad(selectedBook, bookCutList.inventories);
    } else {
      showToast({type: 'error', message: "Scan all the boards for this book cut", autoClose: false})
    }
  }

  const selectBook = (book, inventoryList) => {
    setShowBookCut(false);
    if (book.boardcount === inventoryList.length) {
      linkInventoryToBookAndLoad(book, inventoryList);
      setSelectedBook(undefined);
      setBookCutList(undefined);
    } else {
      setSelectedBook(book);
      setBookCutList({
        books: [book],
        inventories: inventoryList
      });
    }
  }

  return (
    <div className={styles.scanView}>
      <div className={styles.spaceBetween}>
        <span className="bold">Scan Details</span>
        <div>
          {/* <Button type={'primary'} onClick={() => onBarcodeScan()}>On Scan</Button> */}
          {!boardScanData.book && <Button type={'secondary'} fillStyle={'outline'} onClick={() => setAddManually(true)}>
            <MaterialIconsReact icon={'add'} size={20}></MaterialIconsReact>
            <span className={styles.ml8}>Add Manually</span>
          </Button>}
        </div>
      </div>
      {boardScanData.book ? <ScanCard addManually={() => setAddManually(true)} boardScanData={boardScanData} finishedParts={finishedParts}></ScanCard> : <InfoMessage message='Scan a board to start the process'></InfoMessage>
      }
      {showBookCut && <Modal title={'Select book cutting option'} onClose={() => {
        setBookCutList(undefined);
        setShowBookCut(false);
      }}>
        {bookCutList.books.map((item, index) => {
          return (
            <div key={index} className={styles.bookCutOption} onClick={() => selectBook(item, bookCutList.inventories)}>
              {item.boardcount === 1 ? <span>{item.boardcount} Board</span> : <span>{item.boardcount} Boards</span>}
              <MaterialIconsReact icon={'chevron_right'} size={20}></MaterialIconsReact>
            </div>
          )
        })}
      </Modal>}
      {selectedBook && 
      <Modal 
        title={`${selectedBook.boardcount} Boards in similar pattern`} 
        onClose={() => {
          setBookCutList(undefined);
          setSelectedBook(undefined)
        }}
        footerSlot={<div className={styles.footerWrapper}>
          <div>
            {/* <a className={styles.hyperlink} onClick={continueSingle}>Cut single board</a> */}
            <a className={styles.hyperlink} onClick={() => setAddManually(true)} style={{marginLeft: '12px'}}>Add Manually</a>
          </div>
          <div>
            <Button type={'secondary'} onClick={() => {
              setBookCutList(undefined);
              setSelectedBook(undefined)
            }}>Cancel</Button>
            <Button 
              onClick={() => handleCutAll()} 
              disabled={bookCutList.inventories.length < selectedBook.boardcount} 
              type={'primary'} 
              style={{marginLeft: '12px'}}
              >Cut All Boards</Button>
          </div>
        </div>}
      >
        {bookCutList.inventories.length < selectedBook.boardcount && <InfoMessage message={`Scan remaining ${selectedBook.boardcount - bookCutList.inventories.length} boards`}></InfoMessage>}
        <div className={'table-responsive ' + styles.mt16}>
          <table className="table block">
            <thead style={{border: '1px solid #CCCCCC', backgroundColor: '#F2F2F2'}}>
              <tr>
                <th className="ft-12">Board Details</th>
                <th className="ft-12">Status</th>
              </tr>
            </thead>
            <tbody style={{border: '1px solid #E6E6E6'}}>
              {
                [...Array(selectedBook.boardcount)].map((item, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        <div style={{display: 'flex', flexDirection: 'row'}}>
                          <div title={bookCutList.inventories[0].detail?.core_material}>
                            <div>{bookCutList.inventories[0].detail?.notes}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {index + 1 <= bookCutList.inventories.length ? 
                        <Tag type='primary' fillStyle='outline' icon={'check_circle'}>Scanned</Tag> : 
                        <Tag type='tertiary' fillStyle='filled'>Pending</Tag>}
                      </td>
                    </tr>
                  )
                })
              }
            </tbody>
          </table>
        </div>
      </Modal>}
      {addManully && <Modal title={'Add Board/Panel'} onClose={() => setAddManually(false)} primaryAction={addBarCodeManul}>
        <label htmlFor="panelNumber" className={styles.inputLabel}>Enter barcode number</label>
        <Input id="panelNumber" placeholder='Enter barcode number' onChange={(e) => setBarcode(e.target.value)}></Input>
      </Modal>}
    </div>
  );
}

export default ScanView;
